/**
 * Two-pane scheduling UI: a horizontally-scrolling day strip on the left
 * and a time-of-day-grouped slot grid on the right.
 *
 * The day strip is the small-screen-friendly equivalent of a full month
 * calendar — most beauty/barber bookings happen within the next 7–10
 * days, so showing 14 days as scrollable chips converts better than a
 * full month grid that hides today behind a navigation control.
 *
 * Slots are grouped into morning / afternoon / evening buckets because
 * scanning a 60-slot flat list is exhausting on mobile.
 */

import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { formatYMD } from "./mockSalonData";

function isToday(date) {
  const t = new Date();
  return (
    date.getFullYear() === t.getFullYear() &&
    date.getMonth() === t.getMonth() &&
    date.getDate() === t.getDate()
  );
}

function isTomorrow(date) {
  const t = new Date();
  t.setDate(t.getDate() + 1);
  return (
    date.getFullYear() === t.getFullYear() &&
    date.getMonth() === t.getMonth() &&
    date.getDate() === t.getDate()
  );
}

const WEEKDAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"];
const MONTH_KEYS = [
  "jan", "feb", "mar", "apr", "may", "jun",
  "jul", "aug", "sep", "oct", "nov", "dec",
];

function DayChip({ palette, date, t, lang, isSelected, slotsCount, onClick }) {
  const weekdayKey = WEEKDAY_KEYS[date.getDay()];
  const monthKey = MONTH_KEYS[date.getMonth()];
  const dayNum = date.getDate();
  const relative = isToday(date)
    ? t("salonBooking.datetime.today")
    : isTomorrow(date)
      ? t("salonBooking.datetime.tomorrow")
      : t(`salonBooking.weekdays.${weekdayKey}`);
  void lang;

  const noSlots = slotsCount === 0;

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={noSlots}
      className="text-center"
      style={{
        flexShrink: 0,
        minWidth: 76,
        padding: "10px 12px",
        borderRadius: 14,
        background: isSelected ? palette.accent : palette.surface,
        color: isSelected ? "#fff" : noSlots ? palette.textSubtle : palette.text,
        border: isSelected
          ? `1.5px solid ${palette.accent}`
          : noSlots
            ? `1px dashed ${palette.textSubtle}`
            : palette.border,
        cursor: noSlots ? "not-allowed" : "pointer",
        opacity: noSlots ? 0.55 : 1,
        display: "flex",
        flexDirection: "column",
        gap: 2,
        transition: "all 0.15s ease",
      }}
    >
      <span
        style={{
          fontSize: "0.72rem",
          textTransform: "uppercase",
          letterSpacing: "0.05em",
          opacity: 0.85,
        }}
      >
        {relative}
      </span>
      <span
        className="fw-700"
        style={{
          fontSize: "1.4rem",
          lineHeight: 1.1,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {dayNum}
      </span>
      <span style={{ fontSize: "0.75rem", opacity: 0.85 }}>
        {t(`salonBooking.months.${monthKey}`)}
      </span>
      {noSlots ? (
        <span style={{ fontSize: "0.65rem", marginTop: 2 }}>
          {t("salonBooking.datetime.noFreeShort")}
        </span>
      ) : null}
    </button>
  );
}

function bucketLabel(time) {
  const [h] = time.split(":").map(Number);
  if (h < 12) return "morning";
  if (h < 17) return "afternoon";
  return "evening";
}

function SlotBucket({ palette, label, slots, selectedTime, onSelect }) {
  if (slots.length === 0) return null;
  return (
    <div>
      <div
        className="fw-600 mb-2"
        style={{
          color: palette.textMuted,
          fontSize: "0.78rem",
          textTransform: "uppercase",
          letterSpacing: "0.06em",
        }}
      >
        {label}
      </div>
      <div className="d-flex flex-wrap gap-2">
        {slots.map((slot) => {
          const isSelected = selectedTime === slot.time;
          return (
            <button
              key={`${slot.time}-${slot.masterId || "fixed"}`}
              type="button"
              onClick={() => onSelect(slot)}
              className="fw-600"
              style={{
                minWidth: 78,
                padding: "8px 14px",
                borderRadius: 999,
                background: isSelected ? palette.accent : palette.surface,
                color: isSelected ? "#fff" : palette.text,
                border: isSelected
                  ? `1.5px solid ${palette.accent}`
                  : palette.border,
                cursor: "pointer",
                fontSize: "0.95rem",
                fontVariantNumeric: "tabular-nums",
                transition: "all 0.12s ease",
              }}
            >
              {slot.time}
            </button>
          );
        })}
      </div>
    </div>
  );
}

/**
 * `days`: result of `generateAvailability` / `generateAnyMasterAvailability`.
 * Each entry's `slots` may be either an array of `"HH:mm"` strings (fixed
 * master) or an array of `{ time, masterId }` objects (any-master mode).
 * We normalize both shapes to `{ time, masterId? }` internally.
 *
 * `value` is the currently-selected slot, shape `{ date, time, masterId? }`.
 */
export function DateTimePicker({ palette, lang, days, value, onChange }) {
  const { t } = useTranslation();

  const normalisedDays = useMemo(() => {
    return days.map((d) => ({
      ...d,
      slots: d.slots.map((s) =>
        typeof s === "string" ? { time: s, masterId: null } : s,
      ),
    }));
  }, [days]);

  const [activeDate, setActiveDate] = useState(
    value?.date || normalisedDays[0]?.date || null,
  );

  useEffect(() => {
    if (!activeDate && normalisedDays[0]?.date) {
      setActiveDate(normalisedDays[0].date);
    }
    if (
      activeDate &&
      !normalisedDays.find((d) => d.date === activeDate)
    ) {
      setActiveDate(normalisedDays[0]?.date || null);
    }
  }, [normalisedDays, activeDate]);

  const today = useMemo(() => {
    const d = new Date();
    d.setHours(0, 0, 0, 0);
    return d;
  }, []);

  const fullStrip = useMemo(() => {
    const strip = [];
    for (let i = 0; i < 14; i += 1) {
      const d = new Date(today);
      d.setDate(today.getDate() + i);
      const ymd = formatYMD(d);
      const dayData = normalisedDays.find((x) => x.date === ymd);
      strip.push({
        date: ymd,
        jsDate: d,
        slotsCount: dayData ? dayData.slots.length : 0,
        slots: dayData ? dayData.slots : [],
      });
    }
    return strip;
  }, [normalisedDays, today]);

  const activeDay = useMemo(
    () => fullStrip.find((d) => d.date === activeDate) || null,
    [fullStrip, activeDate],
  );

  const buckets = useMemo(() => {
    if (!activeDay) return { morning: [], afternoon: [], evening: [] };
    const m = { morning: [], afternoon: [], evening: [] };
    for (const slot of activeDay.slots) {
      m[bucketLabel(slot.time)].push(slot);
    }
    return m;
  }, [activeDay]);

  if (fullStrip.every((d) => d.slotsCount === 0)) {
    return (
      <div
        style={{
          textAlign: "center",
          padding: "32px 16px",
          color: palette.textMuted,
        }}
      >
        <i
          className="las la-calendar-times"
          style={{ fontSize: "2.4rem", color: palette.accent, display: "block", marginBottom: 8 }}
          aria-hidden="true"
        />
        <div className="fw-600" style={{ color: palette.text }}>
          {t("salonBooking.datetime.noSlotsTitle")}
        </div>
        <div style={{ fontSize: "0.9rem", marginTop: 4 }}>
          {t("salonBooking.datetime.noSlotsHint")}
        </div>
      </div>
    );
  }

  return (
    <div style={{ display: "grid", gap: 16 }}>
      <style>
        {`
          .salon-scroll-x {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .salon-scroll-x::-webkit-scrollbar {
            display: none;
          }
        `}
      </style>
      {/* Horizontal day strip */}
      <div
        className="salon-scroll-x"
        style={{
          display: "flex",
          gap: 8,
          overflowX: "auto",
          padding: "4px 2px",
          scrollSnapType: "x mandatory",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {fullStrip.map((day) => (
          <div key={day.date} style={{ scrollSnapAlign: "start" }}>
            <DayChip
              palette={palette}
              date={day.jsDate}
              t={t}
              lang={lang}
              isSelected={activeDate === day.date}
              slotsCount={day.slotsCount}
              onClick={() => setActiveDate(day.date)}
            />
          </div>
        ))}
      </div>

      {/* Slot grid for the selected day */}
      {activeDay && activeDay.slotsCount > 0 ? (
        <div style={{ display: "grid", gap: 14 }}>
          <SlotBucket
            palette={palette}
            label={t("salonBooking.datetime.morning")}
            slots={buckets.morning}
            selectedTime={value?.date === activeDate ? value?.time : null}
            onSelect={(slot) =>
              onChange({
                date: activeDate,
                time: slot.time,
                masterId: slot.masterId,
              })
            }
          />
          <SlotBucket
            palette={palette}
            label={t("salonBooking.datetime.afternoon")}
            slots={buckets.afternoon}
            selectedTime={value?.date === activeDate ? value?.time : null}
            onSelect={(slot) =>
              onChange({
                date: activeDate,
                time: slot.time,
                masterId: slot.masterId,
              })
            }
          />
          <SlotBucket
            palette={palette}
            label={t("salonBooking.datetime.evening")}
            slots={buckets.evening}
            selectedTime={value?.date === activeDate ? value?.time : null}
            onSelect={(slot) =>
              onChange({
                date: activeDate,
                time: slot.time,
                masterId: slot.masterId,
              })
            }
          />
        </div>
      ) : (
        <div
          style={{
            textAlign: "center",
            padding: "24px 16px",
            color: palette.textMuted,
            fontSize: "0.9rem",
          }}
        >
          {t("salonBooking.datetime.pickAnotherDay")}
        </div>
      )}
    </div>
  );
}
